import { useCallback } from "react";

import type { LangCode } from "@/lib/gql/__generated__/graphql";

import { usePlanEmailTemplatesNext } from "@/components/features/items/edit/_shared/ui/components/main/questions/email-templates/reservation/hooks/plan/use-plan-email-templates-next";

import { useProductPlanFormModelContext } from "../../../../context";

export const usePlanEmailTemplates = () => {
	const {
		viewModel,
		onSetViewModel,
		queryData,
		testEmailSender: {
			sendTestMailAtReservationCreate,
			sendTestMailAtReservationUpdate,
			sendTestMailAtReservationCancel,
			sendTestMailAtCheckInBefore07Days,
			sendTestMailAtCheckInBefore01Days,
			sendTestMailAtCheckInBefore00Days,
			sendTestMailAtCheckOutAfter01Days,
		},
	} = useProductPlanFormModelContext();
	const { hotelEmailAddress, basicCustomMailSetting } = queryData;

	const {
		hotelID,
		atCreated,
		atUpdated,
		atCanceled,
		atCheckInBefore_00days,
		atCheckInBefore_01days,
		atCheckInBefore_07days,
		atCheckOutAfter_01days,
	} = viewModel;

	const handleClickedSendTestEmailReservationCreated = useCallback(
		async ({ lang, email }: { lang: LangCode; email: string }) => {
			await sendTestMailAtReservationCreate({
				customMessage: atCreated.text.value ?? "",
				mailAddress: email,
				langCode: lang,
				id: hotelID,
			});
		},
		[atCreated.text?.value, hotelID, sendTestMailAtReservationCreate],
	);

	const handleClickedSendTestEmailReservationUpdated = useCallback(
		async ({ lang, email }: { lang: LangCode; email: string }) => {
			await sendTestMailAtReservationUpdate({
				customMessage: atUpdated.text.value ?? "",
				mailAddress: email,
				langCode: lang,
				id: hotelID,
			});
		},
		[atUpdated.text?.value, hotelID, sendTestMailAtReservationUpdate],
	);

	const handleClickedSendTestEmailReservationCanceled = useCallback(
		async ({ lang, email }: { lang: LangCode; email: string }) => {
			await sendTestMailAtReservationCancel({
				customMessage: atCanceled.text.value ?? "",
				mailAddress: email,
				langCode: lang,
				id: hotelID,
			});
		},
		[atCanceled.text?.value, hotelID, sendTestMailAtReservationCancel],
	);
	const handleClickedSendTestEmailReservationAtCheckInBefore_07days = useCallback(
		async ({ lang, email }: { lang: LangCode; email: string }) => {
			await sendTestMailAtCheckInBefore07Days({
				customMessage: atCheckInBefore_07days.text.value ?? "",
				mailAddress: email,
				langCode: lang,
				id: hotelID,
			});
		},
		[atCheckInBefore_07days.text.value, hotelID, sendTestMailAtCheckInBefore07Days],
	);

	const handleClickedSendTestEmailReservationAtCheckInBefore_01days = useCallback(
		async ({ lang, email }: { lang: LangCode; email: string }) => {
			await sendTestMailAtCheckInBefore01Days({
				customMessage: atCheckInBefore_01days.text.value ?? "",
				mailAddress: email,
				langCode: lang,
				id: hotelID,
			});
		},
		[atCheckInBefore_01days.text.value, hotelID, sendTestMailAtCheckInBefore01Days],
	);

	const handleClickedSendTestEmailReservationAtCheckInBefore_00days = useCallback(
		async ({ lang, email }: { lang: LangCode; email: string }) => {
			await sendTestMailAtCheckInBefore00Days({
				customMessage: atCheckInBefore_00days.text.value ?? "",
				mailAddress: email,
				langCode: lang,
				id: hotelID,
			});
		},
		[atCheckInBefore_00days.text.value, hotelID, sendTestMailAtCheckInBefore00Days],
	);

	const handleClickedSendTestEmailReservationAtCheckOutAfter_01days = useCallback(
		async ({ lang, email }: { lang: LangCode; email: string }) => {
			await sendTestMailAtCheckOutAfter01Days({
				customMessage: atCheckOutAfter_01days.text.value ?? "",
				mailAddress: email,
				langCode: lang,
				id: hotelID,
			});
		},
		[atCheckOutAfter_01days.text.value, hotelID, sendTestMailAtCheckOutAfter01Days],
	);

	return {
		...usePlanEmailTemplatesNext({
			hotelEmailAddress,
			atCreated,
			atUpdated,
			atCanceled,
			atCheckInBefore_00days,
			atCheckInBefore_01days,
			atCheckInBefore_07days,
			atCheckOutAfter_01days,
			onSetViewModel,
		}),
		handleClickedSendTestEmailReservationCreated,
		handleClickedSendTestEmailReservationUpdated,
		handleClickedSendTestEmailReservationCanceled,
		handleClickedSendTestEmailReservationAtCheckInBefore_07days,
		handleClickedSendTestEmailReservationAtCheckInBefore_01days,
		handleClickedSendTestEmailReservationAtCheckInBefore_00days,
		handleClickedSendTestEmailReservationAtCheckOutAfter_01days,
		// 基本メール設定
		basicCustomMailSetting,
	};
};
